import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { useLocation } from "react-router-dom";
import { api, cardImageUrl, publicImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, X } from "lucide-react";

const STAR_LABELS = ["", "Pésimo", "Malo", "Aceptable", "Bueno", "Excelente"];

/**
 * Global rating reminder modal. Mounted once at the app top level; only
 * renders on the home route ("/"). Fetches /ratings/pending on every visit
 * and shows the FIRST pending transaction so the user can rate their
 * counterparty. Closing with X / "Ahora no" hides it for this session only:
 * next time the user lands on home, it reappears — until they submit a
 * rating or the 14-day window expires (server-side filter).
 */
export default function RatingModal() {
  const { user } = useAuth();
  const location = useLocation();
  const [pending, setPending] = useState(null); // first pending order to rate
  const [dismissedThisVisit, setDismissedThisVisit] = useState(false);
  const dismissKey = pending ? `hittcg:rating-dismissed:${pending.order_id}:${pending.role}` : null;

  // Home → primer pendiente; detalle de pedido cerrado → ese pedido.
  const orderMatch = location.pathname.match(/^\/order\/([^/]+)/);
  const orderId = orderMatch && !location.state?.noRatingModal ? orderMatch[1] : null;
  const onHome = location.pathname === "/" || !!orderId;

  useEffect(() => {
    if (!user || !onHome) return;
    setDismissedThisVisit(false);
    api.get("/ratings/pending")
      .then((r) => {
        const list = r.data || [];
        setPending((orderId ? list.find((p) => p.order_id === orderId) : list[0]) || null);
      })
      .catch(() => setPending(null));
  }, [user?.id, onHome, orderId]);

  if (!user || !onHome || dismissedThisVisit || !pending || (!orderId && sessionStorage.getItem(dismissKey))) return null;

  return (
    <RatingModalCard
      key={`${pending.order_id}-${pending.role}`}
      pending={pending}
      onDismiss={() => { sessionStorage.setItem(dismissKey, "1"); setDismissedThisVisit(true); }}
      onSubmitted={() => setPending(null)}
    />
  );
}

export function RatingModalCard({ pending, onDismiss, onSubmitted }) {
  const [stars, setStars] = useState(0);        // ← starts at 0, nothing preselected
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isBuyer = pending.role === "buyer";
  const title = isBuyer ? "Califica tu compra" : "Califica tu venta";
  const otherLabel = isBuyer ? "Vendedor" : "Comprador";
  const visualStars = hover || stars;       // hover previews, click locks
  const labelIndex = hover || stars;
  const canSubmit = stars > 0 && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/ratings", { order_id: pending.order_id, stars, comment });
      toast.success("¡Gracias por calificar! Ayudas a la comunidad a saber con quién trata.");
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la calificación");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      data-testid="rating-modal"
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={submit}
        className="bg-[#111] border border-[#262626] rounded-md max-w-md w-full p-6 space-y-4 relative"
      >
        <button
          type="button"
          data-testid="rating-modal-close"
          onClick={onDismiss}
          className="absolute top-3 right-3 text-neutral-500 hover:text-white"
          aria-label="Cerrar"
        >
          <X size={18} />
        </button>

        <div>
          <h2 className="font-display text-2xl" data-testid="rating-modal-title">{title}</h2>
          <p className="text-xs text-neutral-500 mt-1">
            {isBuyer
              ? "Cuéntanos cómo te fue con el vendedor."
              : "Cuéntanos cómo te fue con el comprador."}
          </p>
        </div>

        {/* Incentive banner — doble ciego explicado con la palanca personal */}
        <div
          className="bg-sky-500/10 border border-sky-500/40 rounded-md p-3 text-sm leading-snug"
          data-testid="rating-modal-incentive"
        >
          <p className="text-white">
            <b>Hasta que ambos se califiquen, ninguna de las dos notas aparece en el perfil.</b>
          </p>
          <p className="text-neutral-300 mt-1">
            Tu calificación (y la que te dieron) solo se muestra en tu perfil cuando <b className="text-white">ambos</b> califican.
            Si no calificas, <b className="text-white">tu propia reputación queda oculta</b>. Califica para que tu nota sea visible.
          </p>
        </div>

        <div
          className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-[#262626] rounded"
          data-testid="rating-modal-context"
        >
          {(pending.card_image_url || pending.photo) && (
            <img
              src={pending.card_image_url ? cardImageUrl(pending.card_image_url) : publicImageUrl(pending.photo)}
              alt=""
              className="w-12 h-16 object-contain rounded"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          )}
          <div className="text-sm min-w-0">
            <div className="text-white truncate">
              {pending.card_name || "—"}{pending.is_auction && <span className="ml-1.5 text-[10px] uppercase text-amber-300">Subasta</span>}{" "}
              {pending.quantity > 1 && (
                <span className="text-neutral-500 text-xs">x{pending.quantity}</span>
              )}
            </div>
            {(pending.language || pending.variant) && (
              <div className="text-neutral-500 text-xs">
                {(pending.language || "").toUpperCase()}
                {pending.variant ? ` · ${pending.variant}` : ""}
              </div>
            )}
            <div className="text-neutral-400 text-xs mt-1">
              {otherLabel}:{" "}
              <b className="text-white" data-testid="rating-modal-counterparty">
                {pending.other_name || "—"}
              </b>
            </div>
          </div>
        </div>

        {/* Stars — start empty, hover previews, click locks. Label appears below. */}
        <div>
          <div className="flex items-center gap-1" data-testid="rating-modal-stars">
            {[1, 2, 3, 4, 5].map((n) => {
              const filled = n <= visualStars;
              return (
                <button
                  key={n}
                  type="button"
                  data-testid={`rating-star-${n}`}
                  onClick={() => setStars(n)}
                  onPointerEnter={(e) => { if (e.pointerType === "mouse") setHover(n); }}
                  onPointerLeave={() => setHover(0)}
                  className="p-1 transition-transform hover:scale-110"
                  aria-label={`${n} estrella${n > 1 ? "s" : ""} — ${STAR_LABELS[n]}`}
                >
                  <Star
                    size={28}
                    className={
                      filled
                        ? "fill-[#EF4444] text-[#EF4444]"
                        : "text-neutral-700"
                    }
                  />
                </button>
              );
            })}
          </div>
          <div
            className="text-xs mt-1 h-4"
            data-testid="rating-modal-label"
          >
            {labelIndex > 0 ? (
              <span className="text-neutral-300">
                {labelIndex} · <b className="text-[#EF4444]">{STAR_LABELS[labelIndex]}</b>
              </span>
            ) : (
              <span className="text-neutral-500">Elige una nota para continuar</span>
            )}
          </div>
        </div>

        <Textarea
          data-testid="rating-modal-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Comentario opcional"
          className="bg-[#171717] border-[#262626]"
        />

        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            data-testid="rating-modal-submit"
            disabled={!canSubmit}
            className="bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <BusyLabel busy={submitting} text="Enviando…">Enviar calificación</BusyLabel>
          </Button>
          <Button
            type="button"
            data-testid="rating-modal-later"
            variant="ghost"
            onClick={onDismiss}
            className="text-neutral-400 hover:text-white"
          >
            Ahora no
          </Button>
        </div>
      </form>
    </div>
  );
}
